import {useState} from 'react';

import {Editor} from '@tiptap/react';
import {useTranslation} from 'react-i18next';

import AddLinkIcon from '@mui/icons-material/AddLink';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import RedoIcon from '@mui/icons-material/Redo';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import UndoIcon from '@mui/icons-material/Undo';
import {Box, IconButton, Popover, TextField, Typography, useTheme} from '@mui/material';

import {EmpionButton} from '../EmpionButton/EmpionButton';
import {EmpionTooltip} from '../EmpionTooltip/EmpionTooltip';

import {orangeColors} from '../../layouts/theme/designSystem';
import {getFieldStyle} from '../../layouts/theme/FieldStyles';
import {isUrlValid} from '../../utils/urlUtils/isUrlValid';

interface Props {
  editor: Editor | null;
  style?: React.CSSProperties;
  borderColor: string;
}

type ActionClicks =
  | 'undo'
  | 'redo'
  | 'toggleBold'
  | 'toggleItalic'
  | 'toggleUnderline'
  | 'toggleStrike'
  | 'toggleBulletList'
  | 'toggleOrderedList'
  | 'unsetLink';

export const RichTextEditorToolbar = ({editor, style, borderColor}: Props) => {
  const {t} = useTranslation();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [linkHref, setLinkHref] = useState('');
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const theme = useTheme();
  const fieldStyle = getFieldStyle(theme);
  const buttonsBgColor = orangeColors[3];
  const popOverButtonsStyle = {
    backgroundColor: orangeColors[5],
    padding: '5px 10px',
    '&:hover': {
      backgroundColor: orangeColors[3],
      boxShadow: 'none',
    },
  };

  const handleLinkButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    if (editor) {
      const link = editor.isActive('link') ? editor.getAttributes('link') : null;
      if (link) {
        setLinkHref(link.href);
      } else {
        setLinkHref('');
      }
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAddLink = () => {
    if (editor) {
      editor.chain().focus().setLink({href: linkHref}).run();
      setLinkHref('');
      handleClose();
    }
  };

  if (!editor) {
    return null;
  }

  const {view, state} = editor;
  const {from, to} = view.state.selection;
  const text = state.doc.textBetween(from, to, '');

  const handleActionClick = (action: ActionClicks) => {
    if (editor) {
      editor.chain().focus()[action]().run();
    }
  };

  return (
    <Box
      data-testid='RichTextEditorToolbar'
      sx={{
        border: `1px solid ${borderColor}`,
        padding: '10px',
        borderRadius: '5px 5px 0 0',
        display: 'flex',
        gap: '10px',
        ...style,
      }}
    >
      <IconButton onClick={() => handleActionClick('undo')}>
        <UndoIcon fontSize='small' />
      </IconButton>
      <IconButton onClick={() => handleActionClick('redo')}>
        <RedoIcon fontSize='small' />
      </IconButton>
      <IconButton
        onClick={() => handleActionClick('toggleBold')}
        sx={{
          backgroundColor: editor?.isActive('bold') ? buttonsBgColor : 'transparent',
        }}
      >
        <FormatBoldIcon fontSize='small' />
      </IconButton>
      <IconButton
        onClick={() => handleActionClick('toggleItalic')}
        sx={{
          backgroundColor: editor?.isActive('italic') ? buttonsBgColor : 'transparent',
        }}
      >
        <FormatItalicIcon fontSize='small' />
      </IconButton>
      <IconButton
        onClick={() => handleActionClick('toggleUnderline')}
        sx={{
          backgroundColor: editor?.isActive('underline') ? buttonsBgColor : 'transparent',
        }}
      >
        <FormatUnderlinedIcon fontSize='small' />
      </IconButton>
      <IconButton
        onClick={() => handleActionClick('toggleStrike')}
        sx={{
          backgroundColor: editor?.isActive('strike') ? buttonsBgColor : 'transparent',
        }}
      >
        <StrikethroughSIcon fontSize='small' />
      </IconButton>
      <IconButton
        onClick={() => handleActionClick('toggleBulletList')}
        sx={{
          backgroundColor: editor?.isActive('bulletList') ? buttonsBgColor : 'transparent',
        }}
      >
        <FormatListBulletedIcon fontSize='small' />
      </IconButton>
      <IconButton
        onClick={() => handleActionClick('toggleOrderedList')}
        sx={{
          backgroundColor: editor?.isActive('orderedList') ? buttonsBgColor : 'transparent',
        }}
      >
        <FormatListNumberedIcon fontSize='small' />
      </IconButton>
      <EmpionTooltip isEnabled={!text} title={t('richTextEditor.linkButtonActions.toolTipDisabled')}>
        <span>
          <IconButton
            aria-describedby={id}
            data-testid='linkButton'
            onClick={handleLinkButtonClick}
            disabled={!text}
            sx={{
              backgroundColor: editor?.isActive('link') ? buttonsBgColor : 'transparent',
            }}
          >
            <AddLinkIcon fontSize='small' />
          </IconButton>
        </span>
      </EmpionTooltip>
      <Popover
        data-testid='simple-popover'
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box
          sx={{
            padding: '10px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          <TextField
            value={linkHref}
            onChange={(e) => setLinkHref(e.target.value)}
            label={t('richTextEditor.linkButtonActions.urlLabel')}
            variant='outlined'
            placeholder='https://empion.io/'
            sx={{
              ...fieldStyle,
              '& .MuiInputLabel-root': {
                fontSize: '14px',
                color: theme.palette.text.primary,
              },
            }}
            size='small'
          />
          <EmpionButton size='small' sx={popOverButtonsStyle} onClick={handleAddLink} disabled={!isUrlValid(linkHref)}>
            <Typography
              variant='body2'
              sx={{
                color: theme.palette.text.primary,
              }}
            >
              {t('richTextEditor.linkButtonActions.addLink')}
            </Typography>
          </EmpionButton>
          {editor?.isActive('link') && (
            <EmpionButton size='small' onClick={() => handleActionClick('unsetLink')} sx={popOverButtonsStyle}>
              <Typography variant='body2' sx={{color: theme.palette.text.primary}}>
                {t('richTextEditor.linkButtonActions.removeLink')}
              </Typography>
            </EmpionButton>
          )}
          {linkHref && (
            <EmpionButton
              size='small'
              sx={popOverButtonsStyle}
              onClick={() => {
                window.open(linkHref, '_blank');
              }}
            >
              <Typography variant='body2' sx={{color: theme.palette.text.primary}}>
                {t('richTextEditor.linkButtonActions.visitLink')}
              </Typography>
            </EmpionButton>
          )}
        </Box>
      </Popover>
    </Box>
  );
};
